import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, Row, Col, Table, Badge, Button, Alert } from 'react-bootstrap';
import { adminAPI } from '../services/api';

const OrderDetail = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await adminAPI.getOrderById(id);
        setOrder(response.data.data);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  const handleUpdateStatus = async (newStatus) => {
    try {
      setUpdatingStatus(true);
      setError('');
      setSuccess('');
      
      const response = await adminAPI.updateOrderStatus(id, newStatus);
      setOrder(response.data.data);
      setSuccess(`Order status updated to ${newStatus}`);
      
      // Auto-clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      console.error('Error updating order status:', err);
      setError('Failed to update order status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <Badge bg="info">Open</Badge>;
      case 'processing':
        return <Badge bg="warning">Processing</Badge>;
      case 'shipped':
        return <Badge bg="success">Shipped</Badge>;
      // Legacy status support
      case 'created':
        return <Badge bg="info">Created</Badge>;
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'paid':
        return <Badge bg="success">Paid</Badge>;
      case 'fulfilled':
        return <Badge bg="primary">Fulfilled</Badge>;
      case 'cancelled':
        return <Badge bg="danger">Cancelled</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  if (loading) {
    return <div className="loading-spinner">Loading order details...</div>;
  }

  if (error) {
    return <div className="alert alert-danger">{error}</div>;
  }

  if (!order) {
    return <div className="alert alert-warning">Order not found</div>;
  }

  return (
    <div>
      {success && (
        <Alert variant="success" className="mb-4" dismissible onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      
      {error && (
        <Alert variant="danger" className="mb-4" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          Order #{order.shopifyOrderNumber || order.shopifyOrderId}{' '}
          {getStatusBadge(order.status)}
        </h2>
        <Link to="/admin/orders">
          <Button variant="outline-secondary">Back to Orders</Button>
        </Link>
      </div>
      
      <Card className="mb-4">
        <Card.Header>Update Order Status</Card.Header>
        <Card.Body>
          <div className="d-flex gap-2">
            <Button 
              variant={order.status === 'open' ? 'info' : 'outline-info'} 
              onClick={() => handleUpdateStatus('open')}
              disabled={updatingStatus || order.status === 'open'}
            >
              Open
            </Button>
            <Button 
              variant={order.status === 'processing' ? 'warning' : 'outline-warning'} 
              onClick={() => handleUpdateStatus('processing')}
              disabled={updatingStatus || order.status === 'processing'}
            >
              Processing
            </Button>
            <Button 
              variant={order.status === 'shipped' ? 'success' : 'outline-success'} 
              onClick={() => handleUpdateStatus('shipped')}
              disabled={updatingStatus || order.status === 'shipped'}
            >
              Shipped
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>Order Information</Card.Header>
            <Card.Body>
              <Table>
                <tbody>
                  <tr>
                    <th>Shopify Order ID:</th>
                    <td>{order.shopifyOrderId}</td>
                  </tr>
                  <tr>
                    <th>Shopify Order Number:</th>
                    <td>{order.shopifyOrderNumber}</td>
                  </tr>
                  <tr>
                    <th>Group Code:</th>
                    <td>{order.groupCode}</td>
                  </tr>
                  <tr>
                    <th>Campaign ID:</th>
                    <td>{order.campaignId}</td>
                  </tr>
                  <tr>
                    <th>Seller Name:</th>
                    <td>{order.studentName}</td>
                  </tr>
                  <tr>
                    <th>Date Created:</th>
                    <td>{new Date(order.createdAt).toLocaleString()}</td>
                  </tr>
                  <tr>
                    <th>Status:</th>
                    <td>{getStatusBadge(order.status)}</td>
                  </tr>
                  <tr>
                    <th>Total Items:</th>
                    <td>{order.items ? order.items.reduce((total, item) => total + item.quantity, 0) : 0}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>Customer Information</Card.Header>
            <Card.Body>
              <Table>
                <tbody>
                  <tr>
                    <th>Name:</th>
                    <td>{order.customerFirstName} {order.customerLastName}</td>
                  </tr>
                  <tr>
                    <th>Email:</th>
                    <td>{order.customerEmail}</td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          <Card className="mb-4">
            <Card.Header>Shipping Information</Card.Header>
            <Card.Body>
              <Table>
                <tbody>
                  <tr>
                    <th>Method:</th>
                    <td>{order.shipping?.method || 'N/A'}</td>
                  </tr>
                  <tr>
                    <th>Price:</th>
                    <td>${order.shipping?.price?.toFixed(2) || '0.00'}</td>
                  </tr>
                  <tr>
                    <th>Address:</th>
                    <td>
                      {order.shipping?.address1}<br />
                      {order.shipping?.address2 && <>{order.shipping.address2}<br /></>}
                      {order.shipping?.city}, {order.shipping?.province} {order.shipping?.zip}<br />
                      {order.shipping?.country}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Header>Order Items</Card.Header>
        <Card.Body>
          <Table striped responsive>
            <thead>
              <tr>
                <th>Product</th>
                <th>Price</th>
                <th>Quantity</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items && order.items.map((item, index) => (
                <tr key={index}>
                  <td>{item.productName}</td>
                  <td>${item.price.toFixed(2)}</td>
                  <td>{item.quantity}</td>
                  <td>${(item.price * item.quantity).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan="3" className="text-end">Product Total:</th>
                <th>${order.totalProductAmount?.toFixed(2) || '0.00'}</th>
              </tr>
              <tr>
                <th colSpan="3" className="text-end">Shipping:</th>
                <th>${order.shipping?.price?.toFixed(2) || '0.00'}</th>
              </tr>
              <tr>
                <th colSpan="3" className="text-end">Order Total:</th>
                <th>${order.totalAmount.toFixed(2)}</th>
              </tr>
            </tfoot>
          </Table>
        </Card.Body>
      </Card>
    </div>
  );
};

export default OrderDetail;
